import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Pause } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { startPolling } from '@/utils/layoutPolling';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Pop up bisa ditutup, tapi muncul lagi setelah jeda ini sampai report terisi.
const SNOOZE_MS = 30 * 60 * 1000;
// Kalau report sudah beres, cek ulang ke server paling cepat tiap 10 menit
// (cukup untuk mendeteksi pergantian hari tanpa menambah beban).
const RECHECK_DONE_MS = 10 * 60 * 1000;
const RECHECK_PENDING_MS = 5 * 60 * 1000;
const SAVE_TIMEOUT_MS = 25000;

const EMPTY_FORM = {
  inquiry_new_count: '',
  closing_manual_count: '',
  inquiry_vitamin: '',
  inquiry_vaksin: '',
  inquiry_mcu: '',
  inquiry_medicare: '',
  no_response_count: '',
  nyasar_count: '',
};

const digitsOnly = (s) => String(s || '').replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '');
const toInt = (s) => (s === '' || s === undefined ? 0 : parseInt(s, 10) || 0);

const snoozeKey = (userId, date) => `vaxa:mkt_report_snooze:${userId}:${date}`;

function readSnooze(userId, date) {
  try {
    return parseInt(localStorage.getItem(snoozeKey(userId, date)) || '0', 10) || 0;
  } catch (_e) {
    return 0;
  }
}

function writeSnooze(userId, date) {
  try {
    localStorage.setItem(snoozeKey(userId, date), String(Date.now()));
  } catch (_e) {
    /* storage tidak tersedia: abaikan, pop up tetap jalan lewat timer di memori */
  }
}

function formatLongDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

const NumField = ({ label, value, onChange, testId, placeholder, required }) => (
  <div>
    <Label className="text-xs text-gray-600">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </Label>
    <Input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={value}
      onChange={(e) => onChange(digitsOnly(e.target.value))}
      onFocus={(e) => e.target.select()}
      placeholder={placeholder}
      data-testid={testId}
      className="text-center font-mono"
    />
  </div>
);

/**
 * Pop up wajib Report Harian untuk marketing (Okt 2026).
 *
 * Aturan:
 *  - Yang wajib diisi adalah report KEMARIN (WIB), dicek ke backend.
 *  - Bisa ditutup, muncul lagi tiap 30 menit sampai report terisi.
 *  - Tidak muncul saat marketing sedang membuka ruang chat (prop `suppress`).
 *  - Tiga jalur: isi angka (Nomor Masuk + Closing wajib, 0 boleh), rincian
 *    opsional, atau "Off iklan".
 */
export default function MarketingDailyReportPopup({ suppress = false }) {
  const { user } = useAuth();
  // Akun yang hanya meminjam role Marketing (role utama lain, mis. Finance) tidak diwajibkan.
  const isMarketing = user?.role === 'marketing' && (!user?.primary_role || user.primary_role === 'marketing');
  const userId = user?.user_id;

  const [pending, setPending] = useState(null); // { required, report_date }
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const [pauseUntil, setPauseUntil] = useState('');
  const [pauseBusy, setPauseBusy] = useState(false);

  // Ref supaya callback polling selalu membaca nilai terbaru tanpa restart.
  const pendingRef = useRef(null);
  const suppressRef = useRef(suppress);
  const openRef = useRef(false);
  const lastCheckRef = useRef(0);
  const closedAtRef = useRef({}); // cadangan snooze di memori per tanggal

  useEffect(() => { suppressRef.current = suppress; }, [suppress]);
  useEffect(() => { openRef.current = open; }, [open]);

  const maybeOpen = useCallback(() => {
    const p = pendingRef.current;
    if (!p || !p.required || openRef.current || suppressRef.current) return;
    const lastClosed = Math.max(
      readSnooze(userId, p.report_date),
      closedAtRef.current[p.report_date] || 0,
    );
    if (Date.now() - lastClosed < SNOOZE_MS) return;
    // Isian yang sempat diketik tetap disimpan saat pop up ditutup; baru
    // dikosongkan setelah berhasil disimpan.
    setConfirmOff(false);
    setOpen(true);
  }, [userId]);

  const fetchPending = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return true;
    try {
      const res = await axios.get(`${API}/v3/reports/marketing-daily/my-pending`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 25000,
      });
      lastCheckRef.current = Date.now();
      pendingRef.current = res.data;
      setPending(res.data);
      return true;
    } catch (_e) {
      // Gagal diam-diam (sinyal lemah): putaran berikutnya mencoba lagi.
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isMarketing || !userId) return undefined;
    const run = async () => {
      const p = pendingRef.current;
      // Cek ke server jarang saja: tiap 5 menit selama belum lapor (bisa saja
      // sudah diisi dari perangkat lain), tiap 10 menit kalau sudah beres.
      const gap = p?.required ? RECHECK_PENDING_MS : RECHECK_DONE_MS;
      const needFetch = !p || Date.now() - lastCheckRef.current >= gap;
      let ok = true;
      if (needFetch) ok = await fetchPending();
      maybeOpen();
      return ok;
    };
    // Tunda sedikit supaya tidak berebut dengan gelombang fetch awal layout.
    let stop = null;
    const startTimer = setTimeout(() => {
      stop = startPolling(run, { intervalMs: 30000 });
    }, 1500);
    return () => {
      clearTimeout(startTimer);
      if (stop) stop();
    };
  }, [isMarketing, userId, fetchPending, maybeOpen]);

  // Saat marketing keluar dari ruang chat, cek lagi tanpa menunggu putaran.
  useEffect(() => {
    if (!suppress) maybeOpen();
  }, [suppress, maybeOpen]);

  const handleClose = () => {
    const p = pendingRef.current;
    if (p?.report_date) {
      closedAtRef.current[p.report_date] = Date.now();
      writeSnooze(userId, p.report_date);
    }
    setOpen(false);
    setConfirmOff(false);
  };

  const setField = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  const canSave = form.inquiry_new_count !== '' && form.closing_manual_count !== '' && !saving;

  const submit = async (payload, successMsg) => {
    const token = localStorage.getItem('token');
    setSaving(true);
    try {
      await axios.post(
        `${API}/v3/reports/marketing-daily/manual`,
        {
          report_date: pending.report_date,
          marketing_id: userId,
          ...payload,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: SAVE_TIMEOUT_MS },
      );
      const done = { ...pending, required: false };
      pendingRef.current = done;
      lastCheckRef.current = Date.now();
      setPending(done);
      setOpen(false);
      setConfirmOff(false);
      setForm(EMPTY_FORM);
      toast.success(successMsg);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Gagal menyimpan. Cek sinyal lalu coba lagi, isian kamu masih aman.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNumbers = () => {
    if (!canSave) return;
    submit(
      {
        report_status: 'reported',
        inquiry_new_count: toInt(form.inquiry_new_count),
        closing_manual_count: toInt(form.closing_manual_count),
        inquiry_vitamin: toInt(form.inquiry_vitamin),
        inquiry_vaksin: toInt(form.inquiry_vaksin),
        inquiry_mcu: toInt(form.inquiry_mcu),
        inquiry_medicare: toInt(form.inquiry_medicare),
        no_response_count: toInt(form.no_response_count),
        nyasar_count: toInt(form.nyasar_count),
      },
      'Report tersimpan',
    );
  };

  const handleOffIklan = () => {
    if (saving) return;
    submit({ report_status: 'off_iklan' }, 'Tercatat: off iklan');
  };

  const ymd = (d) => {
    const p2 = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  };
  const todayStr = ymd(new Date());
  const maxPauseStr = ymd(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));

  const handlePause = async () => {
    if (!pauseUntil || pauseBusy) return;
    const token = localStorage.getItem('token');
    setPauseBusy(true);
    try {
      await axios.post(
        `${API}/v3/reports/marketing-daily/ads-pause`,
        { until: pauseUntil },
        { headers: { Authorization: `Bearer ${token}` }, timeout: SAVE_TIMEOUT_MS },
      );
      setOpen(false);
      setShowPause(false);
      setPauseUntil('');
      await fetchPending();
      toast.success(`Tercatat: iklan dimatikan sampai ${formatLongDate(pauseUntil)}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Gagal menyimpan. Cek sinyal lalu coba lagi.');
    } finally {
      setPauseBusy(false);
    }
  };

  const handleResume = async () => {
    if (pauseBusy) return;
    const token = localStorage.getItem('token');
    setPauseBusy(true);
    try {
      await axios.delete(`${API}/v3/reports/marketing-daily/ads-pause`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: SAVE_TIMEOUT_MS,
      });
      await fetchPending();
      toast.success('Tercatat: iklan sudah nyala lagi. Report harian wajib lagi mulai hari ini.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Gagal menyimpan. Cek sinyal lalu coba lagi.');
    } finally {
      setPauseBusy(false);
    }
  };

  if (!isMarketing) return null;

  const banner = pending?.paused_until ? (
    <div
      className="fixed bottom-4 left-4 right-20 z-30 max-w-sm rounded-lg border border-amber-300 bg-amber-50 p-3 shadow-md"
      data-testid="ads-pause-banner"
    >
      <p className="text-sm text-amber-900">
        Iklan dimatikan sampai {formatLongDate(pending.paused_until)}. Report harian tidak diwajibkan selama itu.
      </p>
      <Button
        size="sm"
        variant="outline"
        className="mt-2 w-full bg-white"
        onClick={handleResume}
        disabled={pauseBusy}
        data-testid="ads-resume-button"
      >
        {pauseBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Iklan sudah nyala lagi
      </Button>
    </div>
  ) : null;

  if (!pending?.required) return banner;

  return (
    <>
    {banner}
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="marketing-report-popup"
        // Tap di luar kotak tidak menutup (isian tidak hilang karena salah sentuh).
        // Tombol X dan Escape tetap bisa menutup, lalu muncul lagi 30 menit kemudian.
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Report Harian Belum Diisi</DialogTitle>
          <DialogDescription>
            Tanggal: {formatLongDate(pending.report_date)}. Wajib diisi. Kalau ditutup, pop up muncul lagi 30 menit kemudian.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <NumField
              label="Nomor Masuk (Inquiry)"
              value={form.inquiry_new_count}
              onChange={setField('inquiry_new_count')}
              placeholder="0 jika tidak ada"
              testId="popup-input-inquiry"
              required
            />
            <NumField
              label="Closing (Manual)"
              value={form.closing_manual_count}
              onChange={setField('closing_manual_count')}
              placeholder="0 jika tidak ada"
              testId="popup-input-closing"
              required
            />
          </div>

          <div className="border rounded-lg p-3">
            <Label className="text-sm font-medium">Rincian Inquiry (boleh dikosongkan)</Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              <NumField label="Vitamin" value={form.inquiry_vitamin} onChange={setField('inquiry_vitamin')} placeholder="0" testId="popup-input-vitamin" />
              <NumField label="Vaksin" value={form.inquiry_vaksin} onChange={setField('inquiry_vaksin')} placeholder="0" testId="popup-input-vaksin" />
              <NumField label="MCU" value={form.inquiry_mcu} onChange={setField('inquiry_mcu')} placeholder="0" testId="popup-input-mcu" />
              <NumField label="Medicare" value={form.inquiry_medicare} onChange={setField('inquiry_medicare')} placeholder="0" testId="popup-input-medicare" />
            </div>
          </div>

          <div className="border rounded-lg p-3">
            <Label className="text-sm font-medium">Hasil Inquiry (boleh dikosongkan)</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <NumField label="No Response" value={form.no_response_count} onChange={setField('no_response_count')} placeholder="0" testId="popup-input-noresp" />
              <NumField label="Nyasar" value={form.nyasar_count} onChange={setField('nyasar_count')} placeholder="0" testId="popup-input-nyasar" />
            </div>
          </div>

          <Button
            className="w-full"
            onClick={handleSaveNumbers}
            disabled={!canSave}
            data-testid="popup-save-report"
          >
            {saving && !confirmOff ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Simpan Report
          </Button>
          {!canSave && !saving && (
            <p className="text-xs text-gray-500 text-center -mt-2">
              Nomor Masuk dan Closing wajib diisi. Ketik 0 kalau memang tidak ada.
            </p>
          )}

          <div className="border-t pt-3 space-y-2">
            {!showPause ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowPause(true)}
                disabled={saving || confirmOff}
                data-testid="popup-ads-pause-open"
              >
                <Pause className="w-4 h-4 mr-2" />
                Iklan sedang dimatikan beberapa hari
              </Button>
            ) : (
              <div className="space-y-2 rounded-lg border p-3">
                <p className="text-sm text-gray-700">
                  Iklan dimatikan sampai tanggal berapa? Maksimal 14 hari. Selama itu pop up report berhenti muncul.
                </p>
                <Input
                  type="date"
                  value={pauseUntil}
                  min={todayStr}
                  max={maxPauseStr}
                  onChange={(e) => setPauseUntil(e.target.value)}
                  data-testid="popup-ads-pause-date"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => { setShowPause(false); setPauseUntil(''); }} disabled={pauseBusy}>
                    Batal
                  </Button>
                  <Button onClick={handlePause} disabled={!pauseUntil || pauseBusy} data-testid="popup-ads-pause-confirm">
                    {pauseBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Simpan
                  </Button>
                </div>
              </div>
            )}
            {!confirmOff ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setConfirmOff(true)}
                disabled={saving}
                data-testid="popup-off-iklan"
              >
                Off iklan (tidak diiklankan hari itu)
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  Catat {formatLongDate(pending.report_date)} sebagai off iklan? Semua angka hari itu dicatat 0.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => setConfirmOff(false)} disabled={saving}>
                    Batal
                  </Button>
                  <Button onClick={handleOffIklan} disabled={saving} data-testid="popup-off-iklan-confirm">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Ya, off iklan
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
